import { useState, useRef } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  CarIcon,
  UploadSimpleIcon,
  XCircleIcon,
  CheckCircleIcon,
  PhoneIcon,
  EnvelopeSimpleIcon,
  UserIcon,
  MapPinIcon,
  CurrencyEurIcon,
  CalendarIcon,
  GaugeIcon,
} from "@phosphor-icons/react";
import { CarGrMakeCombobox, CarGrModelCombobox } from "@/components/CarGrBrandSelect";
import { FUEL_TYPES, TRANSMISSIONS } from "@/components/CarFormFields";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const MAX_PHOTOS = 10;
const MAX_SIZE_MB = 8;

const CONDITIONS = [
  { value: "", label: "— Επιλέξτε —" },
  { value: "Άριστη", label: "Άριστη" },
  { value: "Πολύ καλή", label: "Πολύ καλή" },
  { value: "Καλή", label: "Καλή" },
  { value: "Με μικρές ζημιές", label: "Με μικρές ζημιές" },
  { value: "Χρειάζεται επισκευή", label: "Χρειάζεται επισκευή" },
];

export default function SellMyCar() {
  const [form, setForm] = useState({
    brand: "",
    car_gr_make_id: 0,
    model: "",
    car_gr_model_id: 0,
    variant: "",
    year: "",
    mileage_km: "",
    fuel_type: "",
    transmission: "",
    vin: "",
    condition: "",
    desired_price_eur: "",
    name: "",
    phone: "",
    email: "",
    city: "",
    comment: "",
    website: "", // honeypot
  });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const fileInputRef = useRef(null);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const addFiles = (incoming) => {
    const arr = Array.from(incoming || []);
    const next = [];
    for (const f of arr) {
      if (files.length + next.length >= MAX_PHOTOS) {
        toast.warning(`Έως ${MAX_PHOTOS} φωτογραφίες`);
        break;
      }
      if (f.size > MAX_SIZE_MB * 1024 * 1024) {
        toast.warning(`Το αρχείο ${f.name} είναι μεγαλύτερο από ${MAX_SIZE_MB}MB`);
        continue;
      }
      if (!/^image\/(jpe?g|png|webp)$/i.test(f.type)) {
        toast.warning(`Μη υποστηριζόμενος τύπος: ${f.name}`);
        continue;
      }
      const uid = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      next.push({ uid, file: f, preview: URL.createObjectURL(f) });
    }
    setFiles((cur) => [...cur, ...next]);
  };

  const removeFile = (idx) => {
    setFiles((cur) => {
      const x = [...cur];
      URL.revokeObjectURL(x[idx].preview);
      x.splice(idx, 1);
      return x;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.brand || !form.model || !form.year || !form.mileage_km) {
      toast.error("Συμπληρώστε μάρκα, μοντέλο, έτος και χιλιόμετρα");
      return;
    }
    if (!form.name || !form.phone) {
      toast.error("Συμπληρώστε όνομα και τηλέφωνο επικοινωνίας");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("brand", form.brand);
      fd.append("model", form.model);
      fd.append("variant", form.variant || "");
      fd.append("year", String(Number(form.year) || 0));
      fd.append("mileage_km", String(Number(form.mileage_km) || 0));
      fd.append("fuel_type", form.fuel_type || "");
      fd.append("transmission", form.transmission || "");
      fd.append("vin", form.vin || "");
      fd.append("condition", form.condition || "");
      fd.append("desired_price_eur", String(Number(form.desired_price_eur) || 0));
      fd.append("name", form.name);
      fd.append("phone", form.phone);
      fd.append("email", form.email || "");
      fd.append("city", form.city || "");
      fd.append("comment", form.comment || "");
      fd.append("website", form.website || "");
      for (const { file } of files) fd.append("photos", file);
      const r = await axios.post(`${API}/public/sell-leads`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (r.data?.id) {
        setDone(true);
        files.forEach((x) => URL.revokeObjectURL(x.preview));
      } else {
        toast.error("Σφάλμα υποβολής");
      }
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error("Πάρα πολλές υποβολές, δοκιμάστε αργότερα");
      } else {
        toast.error(err?.response?.data?.detail || "Αποτυχία υποβολής");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-white text-[#0A0A0A] flex items-center justify-center px-6 py-12" data-testid="sell-my-car-success">
        <div className="max-w-xl text-center">
          <CheckCircleIcon size={72} weight="fill" className="text-[#16A34A] mx-auto" />
          <h1 className="heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-6">
            Η αίτησή σας υποβλήθηκε!
          </h1>
          <p className="text-[#4B5563] mt-4 text-base">
            Ευχαριστούμε που μας εμπιστευτήκατε. Η ομάδα της <b>BESIKOS CARS</b> θα επικοινωνήσει
            μαζί σας τις επόμενες εργάσιμες ώρες για να συζητήσουμε το αυτοκίνητό σας.
          </p>
          <div className="mt-8">
            <a href="https://besikos.akalemi.gr" className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] font-medium border-b-2 border-[#0A0A0A] pb-1 hover:opacity-70">
              Επιστροφή στο site
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-[#0A0A0A]" data-testid="sell-my-car-page">
      <Toaster position="top-right" richColors />
      {/* Hero */}
      <header className="border-b border-black/10 bg-[#0A0A0A] text-white">
        <div className="max-w-4xl mx-auto px-6 py-10 sm:py-14">
          <div className="flex items-baseline gap-1.5">
            <span className="heading text-xl sm:text-2xl font-extrabold tracking-tight">BESIKOS</span>
            <span className="heading text-xl sm:text-2xl font-light tracking-tight">CARS</span>
          </div>
          <h1 className="heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-6">
            Πούλησέ μας το αυτοκίνητό σου
          </h1>
          <p className="text-white/70 text-base mt-4 max-w-2xl">
            Συμπλήρωσε τη φόρμα και θα επικοινωνήσουμε μαζί σου με μια δίκαιη προσφορά. Άμεση
            αποτίμηση, ξεκάθαρη διαδικασία, χωρίς ταλαιπωρία.
          </p>
        </div>
      </header>

      <form onSubmit={submit} className="max-w-4xl mx-auto px-6 py-10 space-y-8">
        {/* Honeypot — invisible to humans, bots fill it */}
        <input
          type="text"
          name="website"
          value={form.website}
          onChange={(e) => set("website", e.target.value)}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }}
        />

        {/* Vehicle */}
        <FormSection title="Στοιχεία Αυτοκινήτου" icon={CarIcon}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Μάρκα" required>
              <CarGrMakeCombobox
                value={form.brand}
                makeId={form.car_gr_make_id}
                onPick={({ brand_name, make_id }) =>
                  setForm((s) => ({
                    ...s,
                    brand: brand_name,
                    car_gr_make_id: make_id,
                    model: (s.brand || "").toLowerCase() === (brand_name || "").toLowerCase() ? s.model : "",
                    car_gr_model_id: (s.brand || "").toLowerCase() === (brand_name || "").toLowerCase() ? s.car_gr_model_id : 0,
                  }))
                }
                dataTestid="sell-brand"
              />
            </FormField>
            <FormField label="Μοντέλο" required>
              <CarGrModelCombobox
                value={form.model}
                modelId={form.car_gr_model_id}
                makeId={form.car_gr_make_id}
                onPick={({ model_name, model_id }) =>
                  setForm((s) => ({ ...s, model: model_name, car_gr_model_id: model_id }))
                }
                dataTestid="sell-model"
              />
            </FormField>
            <FormField label="Έκδοση / Variant">
              <PlainInput
                value={form.variant}
                onChange={(e) => set("variant", e.target.value)}
                placeholder="π.χ. 1.6 TDI Highline"
              />
            </FormField>
            <FormField label="VIN (προαιρετικό)" hint="17 χαρακτήρες">
              <PlainInput
                data-testid="sell-vin"
                value={form.vin}
                onChange={(e) => set("vin", e.target.value.toUpperCase())}
                maxLength={17}
                className="font-mono uppercase"
              />
            </FormField>
            <FormField label="Έτος" required icon={CalendarIcon}>
              <PlainInput
                data-testid="sell-year"
                type="number"
                value={form.year}
                onChange={(e) => set("year", e.target.value)}
                min={1950}
                max={new Date().getFullYear() + 1}
                placeholder="π.χ. 2018"
              />
            </FormField>
            <FormField label="Χιλιόμετρα" required icon={GaugeIcon}>
              <PlainInput
                data-testid="sell-mileage"
                type="number"
                value={form.mileage_km}
                onChange={(e) => set("mileage_km", e.target.value)}
                min={0}
                placeholder="π.χ. 120000"
              />
            </FormField>
            <FormField label="Καύσιμο">
              <PlainSelect value={form.fuel_type} onChange={(e) => set("fuel_type", e.target.value)}>
                <option value="">— Επιλέξτε —</option>
                {FUEL_TYPES.map((f) => <option key={f} value={f}>{f}</option>)}
              </PlainSelect>
            </FormField>
            <FormField label="Κιβώτιο">
              <PlainSelect value={form.transmission} onChange={(e) => set("transmission", e.target.value)}>
                <option value="">— Επιλέξτε —</option>
                {TRANSMISSIONS.map((t) => <option key={t} value={t}>{t}</option>)}
              </PlainSelect>
            </FormField>
            <FormField label="Γενική Κατάσταση">
              <PlainSelect value={form.condition} onChange={(e) => set("condition", e.target.value)} data-testid="sell-condition">
                {CONDITIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </PlainSelect>
            </FormField>
            <FormField label="Επιθυμητή Τιμή (€)" icon={CurrencyEurIcon}>
              <PlainInput
                type="number"
                value={form.desired_price_eur}
                onChange={(e) => set("desired_price_eur", e.target.value)}
                min={0}
                placeholder="π.χ. 12000"
              />
            </FormField>
          </div>
        </FormSection>

        {/* Photos */}
        <FormSection title="Φωτογραφίες" icon={UploadSimpleIcon} subtitle={`Έως ${MAX_PHOTOS} φωτογραφίες · max ${MAX_SIZE_MB}MB έκαστη`}>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            data-testid="sell-add-photos"
            className="w-full border-2 border-dashed border-black/20 hover:border-[#0A0A0A] hover:bg-[#F9FAFB] transition-colors py-10 flex flex-col items-center gap-2 text-[#4B5563] hover:text-[#0A0A0A]"
          >
            <UploadSimpleIcon size={32} weight="regular" />
            <span className="text-sm font-medium">Σύρετε ή κάντε κλικ για ανέβασμα</span>
            <span className="text-xs">JPG · PNG · WEBP</span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            className="hidden"
            onChange={(e) => addFiles(e.target.files)}
            data-testid="sell-photo-input"
          />
          {files.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 mt-4" data-testid="sell-photo-list">
              {files.map((f, i) => (
                <div key={f.uid} className="relative group border border-black/10 aspect-square overflow-hidden">
                  <img src={f.preview} alt={`Φωτο ${i + 1}`} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    className="absolute top-1 right-1 bg-white/90 hover:bg-white p-1 shadow"
                    aria-label="Αφαίρεση"
                  >
                    <XCircleIcon size={18} weight="fill" className="text-[#DC2626]" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </FormSection>

        {/* Contact */}
        <FormSection title="Στοιχεία Επικοινωνίας" icon={UserIcon}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Ονοματεπώνυμο" required icon={UserIcon}>
              <PlainInput data-testid="sell-name" value={form.name} onChange={(e) => set("name", e.target.value)} />
            </FormField>
            <FormField label="Τηλέφωνο" required icon={PhoneIcon}>
              <PlainInput data-testid="sell-phone" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="π.χ. 6981234567" />
            </FormField>
            <FormField label="Email (προαιρετικό)" icon={EnvelopeSimpleIcon}>
              <PlainInput type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
            </FormField>
            <FormField label="Πόλη" icon={MapPinIcon}>
              <PlainInput value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="π.χ. Αθήνα" />
            </FormField>
            <FormField label="Σχόλιο / Επιπλέον πληροφορίες" className="md:col-span-2">
              <textarea
                data-testid="sell-comment"
                value={form.comment}
                onChange={(e) => set("comment", e.target.value)}
                rows={4}
                placeholder="Service ιστορικό, ατύχημα, εξοπλισμός κ.λπ."
                className="w-full px-3 py-2 border border-black/20 bg-white outline-none focus:border-[#0A0A0A] text-sm"
              />
            </FormField>
          </div>
        </FormSection>

        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 border-t border-black/10">
          <p className="text-xs text-[#4B5563]">
            Με την υποβολή συμφωνείτε με την επεξεργασία των στοιχείων σας αποκλειστικά για επικοινωνία σχετικά με την αγορά του αυτοκινήτου.
          </p>
          <button
            type="submit"
            disabled={submitting}
            data-testid="sell-submit"
            className="bg-[#0A0A0A] hover:bg-[#1F2937] disabled:opacity-50 text-white px-8 py-4 text-sm uppercase tracking-[0.18em] font-medium whitespace-nowrap"
          >
            {submitting ? "Υποβολή..." : "Υποβολή Αίτησης"}
          </button>
        </div>
      </form>
    </div>
  );
}

function FormSection({ title, subtitle, icon: Icon, children }) {
  return (
    <section className="border border-black/10 bg-white">
      <header className="px-5 sm:px-6 py-4 border-b border-black/10 bg-[#F9FAFB] flex items-center gap-3">
        {Icon ? <Icon size={22} weight="regular" /> : null}
        <div>
          <h2 className="heading text-base font-bold">{title}</h2>
          {subtitle ? <div className="text-xs text-[#4B5563] mt-0.5">{subtitle}</div> : null}
        </div>
      </header>
      <div className="p-5 sm:p-6">{children}</div>
    </section>
  );
}

function FormField({ label, required, hint, icon: Icon, className = "", children }) {
  return (
    <div className={className}>
      <label className="block text-[10px] uppercase tracking-[0.18em] text-[#4B5563] mb-1.5 flex items-center gap-1">
        {Icon ? <Icon size={12} weight="regular" /> : null}
        <span>{label}{required ? <span className="text-[#DC2626] ml-0.5">*</span> : null}</span>
      </label>
      {children}
      {hint ? <div className="text-[10px] text-[#4B5563] mt-1 font-mono">{hint}</div> : null}
    </div>
  );
}

function PlainInput({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`w-full px-3 py-2 border border-black/20 bg-white outline-none focus:border-[#0A0A0A] text-sm ${className}`}
    />
  );
}

function PlainSelect({ className = "", children, ...props }) {
  return (
    <select
      {...props}
      className={`w-full px-3 py-2 border border-black/20 bg-white outline-none focus:border-[#0A0A0A] text-sm ${className}`}
    >
      {children}
    </select>
  );
}
